<template>
  <div class="pipeline">
    <!-- <b-loading :is-full-page="false" v-model="isInitiating"></b-loading> -->

    <section class="pipeline__header">
      <PipelineControl
        :tournament="tournament"
        :rounds="rounds"
        :steps="steps"
        :showSteps="showSteps"
        @onToggleSteps="toggleSteps"
      />

      <div
        class="is-flex is-justify-content-space-between is-align-items-center mt-4"
        v-if="nextStep"
      >
        <span class="has-text-primary" @click="scrollTo(nextStep.index)">
          Next Step: {{ nextStep.description }}
        </span>

        <TimerDisplay
          v-if="timerInterval"
          :duration="(nextStep.startTime - now) / 1000"
          :run="true"
        />
      </div>
    </section>

    <section
      class="pipeline__body"
      ref="pipeline"
      v-if="showSteps && steps && steps.length"
    >
      <div
        v-for="round in rounds.filter(
          (r) => r.roundNumber <= tournament.totalRounds
        )"
        :key="`round${round.roundNumber}`"
      >
        <p class="title is-4 mt-5">Round {{ round.roundNumber }}</p>

        <PipelineStep
          v-for="step in steps.filter(
            (s) => s.roundNumber === round.roundNumber
          )"
          :ref="`step${step.index}`"
          :key="step.id"
          :step="step"
          :nextStepIndex="nextStepIndex"
          @onPlay="executeStep(step)"
          @onSkip="skipStep(step)"
          @onReschedule="rescheduleStep(step)"
        />
      </div>
    </section>
    <section v-else-if="automationMessage" class="pipeline__message">
      <p class="title is-6">
        {{ automationMessage.title }}
      </p>
      <p class="subtitle is-6">
        {{ automationMessage.message }}
      </p>
    </section>
  </div>
</template>

<script>
import PipelineStep from "./PipelineStep.vue";
import TimerDisplay from "@/components/TimerDisplay";
import PipelineControl from "./PipelineControl.vue";
import { firestore, functions } from "@/services/firebase";
const runStep = functions.httpsCallable("pipeline-runStep");

export default {
  components: { PipelineStep, TimerDisplay, PipelineControl },
  name: "TournamentPipeline",

  props: ["tournament", "rounds"],

  data() {
    return { showSteps: true, steps: [], now: null, timerInterval: null };
  },

  computed: {
    tournamentRef() {
      return firestore.collection("tournaments").doc(this.tournament.id);
    },

    stepsRef() {
      return this.tournamentRef.collection("steps");
    },

    nextStep() {
      if (!this.steps) return null;
      return this.steps.find(
        (s) =>
          s.status === "scheduled" &&
          s.roundNumber <= this.tournament.totalRounds
      );
    },

    nextStepIndex() {
      if (!this.steps) return null;
      if (this.nextStep) return this.nextStep.index;
      return this.steps.length;
    },

    daysUntilStart() {
      if (!this.tournament.startTime) return null;
      const now = new Date();
      const startTime = this.tournament.startTime.toDate();
      const diffTime = startTime - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },

    shouldShowAutomationMessage() {
      return (
        this.tournament.automate &&
        this.steps.length === 0 &&
        this.tournament.currentRound <= this.tournament.totalRounds &&
        this.daysUntilStart !== null
      );
    },

    automationMessage() {
      if (!this.shouldShowAutomationMessage) return null;

      const days = this.daysUntilStart;

      if (days > 14) {
        return {
          type: "is-info",
          title: "Pipeline Steps Scheduled Automatically",
          message: `Pipeline steps will be automatically scheduled within 14 days before the tournament start time (${days} days remaining).`,
        };
      } else if (days >= 0) {
        return {
          type: "is-warning",
          title: "Waiting for Pipeline Steps",
          message:
            "If no pipeline steps appear within 1 minute, try toggling automation off and back on again.",
        };
      } else {
        return {
          type: "is-danger",
          title: "Tournament Start Time Passed",
          message:
            "Please update the tournament schedule and press SYNC then turn AUTO ON again.",
        };
      }
    },
  },

  watch: {
    nextStep: {
      handler(nextStep) {
        if (nextStep) {
          this.showSteps = true;
          this.scrollTo(nextStep.index);
          this.startTimer();
          return;
        }

        // No more next steps
        this.showSteps = false;
        this.stopTimer();

        const lastStep = this.steps
          .slice()
          .reverse()
          .find((s) => s.status !== "scheduled");
        if (lastStep) this.scrollTo(lastStep.index);
      },
      immediate: true,
    },
  },

  methods: {
    scrollTo(n) {
      setTimeout(() => {
        if (n < 3 && this.$refs.pipeline) {
          return this.$refs.pipeline.scrollTo(0, 0);
        }
        const stepRef = this.$refs[`step${n - 2}`];
        if (stepRef && stepRef.length) {
          stepRef[0].$el.scrollIntoView({
            behavior: "smooth",
          });
        }
      }, 500);
    },

    startTimer() {
      if (this.timerInterval) return;
      this.now = new Date();
      this.timerInterval = setInterval(() => {
        this.now = new Date();
      }, 1000);
    },

    stopTimer() {
      console.log("stopTimer");
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    },

    async executeStep(step) {
      runStep({ tournamentId: this.tournament.id, stepId: step.id })
        .then((result) => {
          const data = result.data;
          this.$buefy.toast.open({
            message: `Round ${step.roundNumber} - Step ${step.stepNumber} ${data.status}`,
            type: data.status === "finished" ? "is-success" : "is-warning",
          });
        })
        .catch((error) => {
          const code = error.code;
          const message = error.message;
          const details = error.details;
          this.$buefy.toast.open({
            message: `Round ${step.roundNumber} - Step ${step.stepNumber} failed: ${code} ${message} ${details}`,
            type: "is-danger",
          });
        });
    },

    skipStep(step) {
      this.stepsRef.doc(step.id).update({ status: "skipped" });
      this.$buefy.toast.open({
        message: `Round ${step.roundNumber} - Step ${step.stepNumber} was skipped`,
        type: "is-info",
      });
    },

    rescheduleStep(step) {
      if (this.nextStepIndex === step.index + 1) {
        this.stepsRef.doc(step.id).update({ status: "scheduled" });
      }
    },

    toggleSteps() {
      this.showSteps = !this.showSteps;
    },
  },

  async created() {
    this.$bind("steps", this.stepsRef);
  },
};
</script>

<style scoped>
.pipeline {
  min-width: 305px;
  max-height: 100vh;
  background-color: white;
  position: relative;
}

.pipeline__header {
  position: sticky;
  top: 0;
  padding: 0.75rem;
  background-color: whitesmoke;
  border-bottom: 2px solid grey;
}

.pipeline__body {
  max-height: 86vh;
  overflow: scroll;
}

.pipeline__message {
  max-width: 300px;
  padding: 0.5rem;
  border-radius: 4px;
}
</style>
